import {Component, effect, inject, input, OnInit, output} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {CustomInputComponent} from '../../../input/custom-input/custom-input.component';
import {CustomSelectInputComponent, SelectOption} from '../../../input/custom-select-input/custom-select-input.component';
import {ImagesService} from '../../../../services/general/images.service';
import {AdminService} from '../../../../services/admin/admin.service';
import {ToastService} from '../../../../services/general/toast.service';
import {Admin} from '../../../../interfaces/responses/admin-response.interfaces';
import {PrimaryButtonComponent} from '../../../buttons/primary-button/primary-button.component';

@Component({
  selector: 'app-add-user-overlay',
  imports: [
    CustomInputComponent,
    CustomSelectInputComponent,
    CommonModule,
    ReactiveFormsModule,
    PrimaryButtonComponent,
  ],
  templateUrl: './add-user-overlay.component.html',
  styleUrl: './add-user-overlay.component.scss'
})
export class AddUserOverlayComponent implements OnInit {
  _images = inject(ImagesService);
  _fb = inject(FormBuilder);
  adminService = inject(AdminService);
  _toast = inject(ToastService);

  admin = input<Admin | null>(null);
  isEdit = input<boolean>(false);
  isLoading: boolean = false;

  roleOptions: SelectOption[] = [];

  adminForm: FormGroup = this._fb.group({
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    otherName: [''],
    email: ['', [Validators.required, Validators.email]],
    mobileNumber: ['', [Validators.required]],
    roleId: ['', [Validators.required]],
  });

  ngOnInit(): void {
    this.adminService.getRoles().subscribe({
      next: (roles) => {
        this.roleOptions = roles.map(role => ({ label: role.name, value: role.id }));
      },
      error: (err) => {
        console.error('Error fetching roles:', err);
        this._toast.error('Failed to load roles');
      }
    });
  }

  // React to changes in the 'admin' input and prefill/reset the form accordingly.
  private prefillFormFromAdmin = effect(() => {
    const adminData = this.admin();
    if (adminData) {
      this.adminForm.patchValue({
        firstName: adminData.firstName ?? '',
        lastName: adminData.lastName ?? '',
        otherName: adminData.otherName ?? '',
        email: adminData.email ?? '',
        mobileNumber: adminData.mobileNumber ?? '',
        roleId: adminData.roleId ?? '',
      });
      this.adminForm.markAsPristine();
      this.adminForm.markAsUntouched();
    } else {
      // reset to empty values explicitly
      this.adminForm.reset({
        firstName: '',
        lastName: '',
        otherName: '',
        email: '',
        mobileNumber: '',
        roleId: '',
      });
      this.adminForm.markAsPristine();
      this.adminForm.markAsUntouched();
    }
  });

  closeUser = output<void>();
  userCreated = output<void>();
  userUpdated = output<void>();

  onClose() {
    // Reset form fields to empty strings
    this.adminForm.reset({
      firstName: '',
      lastName: '',
      otherName: '',
      email: '',
      mobileNumber: '',
      roleId: '',
    });
    this.adminForm.markAsPristine();
    this.adminForm.markAsUntouched();
    this.adminForm.updateValueAndValidity({ onlySelf: false, emitEvent: false });

    this.closeUser.emit();
  }

  onSubmit() {
    if (this.adminForm.invalid) {
      this.adminForm.markAllAsTouched();
      return;
    }
    if (this.admin() && this.isEdit()) {
      this.updateAdmin();
    } else {
      this.createAdmin();
    }
  }

  createAdmin() {
    const formValue = this.adminForm.value;
    const adminData = {
      firstName: formValue.firstName ?? '',
      lastName: formValue.lastName ?? '',
      otherName: formValue.otherName || undefined,
      email: formValue.email ?? '',
      mobileNumber: formValue.mobileNumber ?? '',
      roleId: formValue.roleId ?? '',
    };

    this.isLoading = true;
    this.adminService.createAdmin(adminData).subscribe({
      next: () => {
        this._toast.success('Admin created successfully');
        this.userCreated.emit();
        this.onClose();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error creating admin:', error);
        this._toast.error(error?.message || 'Error creating admin');
        this.isLoading = false;
      }
    });
  }

  updateAdmin() {
    const adminData = this.admin();
    if (!adminData?.id) return;

    const formValue = this.adminForm.value;
    const updateData = {
      firstName: formValue.firstName ?? '',
      lastName: formValue.lastName ?? '',
      otherName: formValue.otherName || undefined,
      email: formValue.email ?? '',
      mobileNumber: formValue.mobileNumber ?? '',
      roleId: formValue.roleId ?? '',
    };

    this.isLoading = true;
    this.adminService.updateAdmin(adminData.id, updateData).subscribe({
      next: () => {
        this._toast.success('Admin updated successfully');
        this.userUpdated.emit();
        this.onClose();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error updating admin:', error);
        this._toast.error(error?.message || 'Error updating admin');
        this.isLoading = false;
      }
    });
  }
}
